import { useCallback, useState } from 'react';

export type GeoStatus = 'idle' | 'prompting' | 'granted' | 'denied' | 'unavailable';

export type GeoState = {
  status: GeoStatus;
  lat?: number;
  lng?: number;
};

const STORAGE_KEY = 'pet-marketplace-last-known-location';
/** Older than this and we'd rather have nothing than tell someone they're 40km from a vet. */
const MAX_AGE_MS = 30 * 60_000;

type Remembered = { lat: number; lng: number; at: number };

/**
 * The last position the shopper granted, shared across components.
 *
 * The hook's state is per-component, so a page that didn't itself ask had no
 * coordinates — Discover would rank by distance and then the vendor you tapped
 * showed none, because the detail page mounted its own empty copy. Rather than
 * re-prompting on every screen (a permission dialog per navigation), the granted
 * position is remembered here and handed to whoever mounts next.
 *
 * sessionStorage, not localStorage: a location is only good for as long as you
 * are plausibly still standing there.
 */
function recall(): Remembered | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Remembered;
    if (typeof parsed?.lat !== 'number' || typeof parsed?.lng !== 'number') return null;
    if (Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function remember(lat: number, lng: number): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ lat, lng, at: Date.now() } satisfies Remembered));
  } catch {
    // A full or blocked storage must not break discovery.
  }
}

function initial_state(): GeoState {
  const last = recall();
  // 'granted' is honest here: the browser permission was granted, and these are
  // the coordinates it gave us. The caller only cares whether it has a position.
  return last ? { status: 'granted', lat: last.lat, lng: last.lng } : { status: 'idle' };
}

/**
 * Thin wrapper over `navigator.geolocation`. Permission-gated and free — no
 * Maps API / geocoding. `request()` triggers the browser prompt; on success the
 * coords are stored so callers can feed them to the distance-aware discovery
 * APIs (`?lat=&lng=` on /user/vendors, /user/vendors/:id and the app tabs).
 */
export function use_geolocation() {
  const [state, set_state] = useState<GeoState>(initial_state);

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      set_state({ status: 'unavailable' });
      return;
    }
    set_state({ status: 'prompting' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        remember(pos.coords.latitude, pos.coords.longitude);
        set_state({ status: 'granted', lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => set_state({ status: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable' }),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 5 * 60_000 },
    );
  }, []);

  return { ...state, request };
}
